"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import type { CARNOT_EFFICIENCY_CALCULATOR_STRINGS } from "@/components/tools/carnot-efficiency-calculator";
import { ReservoirDiagram } from "@/components/tools/physics-fields/carnot-diagram";
import {
  carrySolved,
  DiagramFrame,
  type SolveFieldSpec,
  SolveGrid,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import { useToolNumber } from "@/components/tools/tool-ui";
import {
  type CarnotError,
  type EngineTarget,
  type FlowKnown,
  solveCarnotEngine,
  solveCop,
  solveHeatFlows,
} from "@/lib/tool-kit/physics-fields/carnot";
import {
  copWorking,
  engineWorking,
  flowWorking,
} from "@/lib/tool-kit/physics-fields/carnot-working";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  EFFICIENCY_UNITS,
  ENERGY_UNITS,
  findUnit,
  fromBase,
  TEMPERATURE_UNITS,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof CARNOT_EFFICIENCY_CALCULATOR_STRINGS;

interface PanelProps {
  strings: Strings;
  sig: number;
}

function Failure({ error, strings }: { error: CarnotError; strings: Strings }) {
  const messages: Record<CarnotError, string> = {
    missing: strings.errorMissing,
    "absolute-zero": strings.errorAbsoluteZero,
    "cold-above-hot": strings.errorColdAboveHot,
    "equal-temperatures": strings.errorEqual,
    "efficiency-range": strings.errorEfficiency,
    "non-positive": strings.errorPositive,
    "work-exceeds-heat": strings.errorFlows,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

/** A value in the unit chosen for its box, with that unit's symbol. */
function inUnit(
  value: number,
  unitId: string,
  fmt: Parameters<typeof formatSig>[0],
  sig: number
): string {
  const unit = findUnit(ENERGY_UNITS, unitId);
  return `${formatSig(fmt, fromBase(ENERGY_UNITS, unitId, value), sig)} ${unit.symbol}`;
}

/* ── Maximum efficiency ───────────────────────────────────────────────────── */

type EngineKey = "h" | "c" | "e";

const ENGINE_EXAMPLES: Record<
  string,
  {
    text: Record<EngineKey, string>;
    unit: Record<EngineKey, string>;
    target: EngineTarget;
  }
> = {
  plant: {
    text: { h: "540", c: "25", e: "" },
    unit: { h: "C", c: "C", e: "%" },
    target: "efficiency",
  },
  kelvin: {
    text: { h: "600", c: "300", e: "" },
    unit: { h: "K", c: "K", e: "%" },
    target: "efficiency",
  },
  steam: {
    text: { h: "100", c: "0", e: "" },
    unit: { h: "C", c: "C", e: "%" },
    target: "efficiency",
  },
  hot: {
    text: { h: "", c: "300", e: "40" },
    unit: { h: "K", c: "K", e: "%" },
    target: "hot",
  },
};

export function EnginePanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const start = ENGINE_EXAMPLES.plant;
  const [example, setExample] = useState("plant");
  const [target, setTarget] = useState<EngineTarget>(start.target);
  const [text, setText] = useState<Record<EngineKey, string>>(start.text);
  const [unit, setUnit] = useState<Record<EngineKey, string>>(start.unit);

  const outcome = solveCarnotEngine(
    {
      hot: readQuantity(text.h, TEMPERATURE_UNITS, unit.h),
      cold: readQuantity(text.c, TEMPERATURE_UNITS, unit.c),
      efficiency: readQuantity(text.e, EFFICIENCY_UNITS, unit.e),
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<EngineKey>[] = [
    {
      key: "h",
      label: strings.hot,
      units: TEMPERATURE_UNITS,
      solved: target === "hot",
    },
    {
      key: "c",
      label: strings.cold,
      units: TEMPERATURE_UNITS,
      solved: target === "cold",
    },
    {
      key: "e",
      label: strings.efficiency,
      units: EFFICIENCY_UNITS,
      solved: target === "efficiency",
    },
  ];
  const solvedSI = {
    h: value?.hot ?? null,
    c: value?.cold ?? null,
    e: value?.efficiency ?? null,
  };
  const names = strings.engineExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = ENGINE_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
      setTarget(ex.target);
    }
  };

  return (
    <>
      <SelectField
        id="ce-engine-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(ENGINE_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.solveFor}
        name="ce-engine-solve"
        onChange={(next: EngineTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "efficiency", label: strings.efficiencyShort },
          { value: "hot", label: strings.hotShort },
          { value: "cold", label: strings.coldShort },
        ]}
        value={target}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="ce-engine"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.maxEfficiency}
                value={`${formatSig(fmt, value.efficiency * 100, sig)}%`}
              />
              <StatCard
                label={strings.workPer100}
                value={`${formatSig(fmt, value.efficiency * 100, sig)} J`}
              />
              <StatCard
                label={strings.rejectedPer100}
                value={`${formatSig(fmt, value.rejectedShare * 100, sig)} J`}
              />
              <StatCard
                label={strings.hot}
                value={`${formatSig(fmt, value.hot, sig)} K`}
              />
              <StatCard
                label={strings.cold}
                value={`${formatSig(fmt, value.cold, sig)} K`}
              />
              <StatCard
                label={strings.temperatureGap}
                value={`${formatSig(fmt, value.hot - value.cold, sig)} K`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={engineWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.engineCaption}>
              <ReservoirDiagram
                coldShare={value.rejectedShare}
                description={strings.engineDescription}
                hotShare={1}
                id="ce-engine-diagram"
                label={strings.engineLabel}
                mode="engine"
                workShare={value.efficiency}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── Refrigerators and heat pumps ─────────────────────────────────────────── */

type CopKey = "h" | "c" | "w";

const COP_EXAMPLES: Record<
  string,
  { text: Record<CopKey, string>; unit: Record<CopKey, string> }
> = {
  fridge: {
    text: { h: "25", c: "4", w: "" },
    unit: { h: "C", c: "C", w: "kWh" },
  },
  freezer: {
    text: { h: "25", c: "-18", w: "" },
    unit: { h: "C", c: "C", w: "kWh" },
  },
  pump: {
    text: { h: "20", c: "0", w: "1" },
    unit: { h: "C", c: "C", w: "kWh" },
  },
};

export function CopPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("fridge");
  const [text, setText] = useState<Record<CopKey, string>>(
    COP_EXAMPLES.fridge.text
  );
  const [unit, setUnit] = useState<Record<CopKey, string>>(
    COP_EXAMPLES.fridge.unit
  );

  const outcome = solveCop(
    readQuantity(text.h, TEMPERATURE_UNITS, unit.h),
    readQuantity(text.c, TEMPERATURE_UNITS, unit.c),
    readQuantity(text.w, ENERGY_UNITS, unit.w)
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<CopKey>[] = [
    {
      key: "h",
      label: strings.hotSide,
      units: TEMPERATURE_UNITS,
      solved: false,
    },
    {
      key: "c",
      label: strings.coldSide,
      units: TEMPERATURE_UNITS,
      solved: false,
    },
    {
      key: "w",
      label: strings.workInput,
      units: ENERGY_UNITS,
      solved: false,
      hint: strings.workInputHint,
    },
  ];
  const names = strings.copExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = COP_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
    }
  };

  return (
    <>
      <SelectField
        id="ce-cop-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(COP_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="ce-cop"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={{}}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.copCooling}
                value={formatSig(fmt, value.copCooling, sig)}
              />
              <StatCard
                label={strings.copHeating}
                value={formatSig(fmt, value.copHeating, sig)}
              />
              <StatCard
                label={strings.temperatureGap}
                value={`${formatSig(fmt, value.hot - value.cold, sig)} K`}
              />
              <StatCard
                label={strings.heatRemoved}
                value={
                  value.heatRemoved === null
                    ? "—"
                    : inUnit(value.heatRemoved, unit.w, fmt, sig)
                }
              />
              <StatCard
                label={strings.heatDelivered}
                value={
                  value.heatDelivered === null
                    ? "—"
                    : inUnit(value.heatDelivered, unit.w, fmt, sig)
                }
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={copWorking(value, fmt, sig)}
            />
            <DiagramFrame caption={strings.coolerCaption}>
              <ReservoirDiagram
                coldShare={value.copCooling / value.copHeating}
                description={strings.coolerDescription}
                hotShare={1}
                id="ce-cop-diagram"
                label={strings.coolerLabel}
                mode="cooler"
                workShare={1 / value.copHeating}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── Heat flows ───────────────────────────────────────────────────────────── */

type FlowKey = "h" | "c" | "q" | "x";

const FLOW_EXAMPLES: Record<
  string,
  { text: Record<FlowKey, string>; known: FlowKnown }
> = {
  allowed: {
    text: { h: "600", c: "300", q: "1000", x: "700" },
    known: "rejected",
  },
  impossible: {
    text: { h: "600", c: "300", q: "1000", x: "600" },
    known: "work",
  },
  carnot: {
    text: { h: "600", c: "300", q: "1000", x: "500" },
    known: "work",
  },
};

export function FlowPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("allowed");
  const [known, setKnown] = useState<FlowKnown>("rejected");
  const [text, setText] = useState<Record<FlowKey, string>>(
    FLOW_EXAMPLES.allowed.text
  );
  const [unit, setUnit] = useState<Record<FlowKey, string>>({
    h: "K",
    c: "K",
    q: "J",
    x: "J",
  });

  const outcome = solveHeatFlows(
    readQuantity(text.h, TEMPERATURE_UNITS, unit.h),
    readQuantity(text.c, TEMPERATURE_UNITS, unit.c),
    readQuantity(text.q, ENERGY_UNITS, unit.q),
    known,
    readQuantity(text.x, ENERGY_UNITS, unit.x)
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<FlowKey>[] = [
    {
      key: "h",
      label: strings.hotSide,
      units: TEMPERATURE_UNITS,
      solved: false,
    },
    {
      key: "c",
      label: strings.coldSide,
      units: TEMPERATURE_UNITS,
      solved: false,
    },
    {
      key: "q",
      label: strings.heatAbsorbed,
      units: ENERGY_UNITS,
      solved: false,
    },
    {
      key: "x",
      label: known === "rejected" ? strings.heatRejected : strings.workDone,
      units: ENERGY_UNITS,
      solved: false,
    },
  ];
  const names = strings.flowExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = FLOW_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setKnown(ex.known);
      setUnit({ h: "K", c: "K", q: "J", x: "J" });
    }
  };

  return (
    <>
      <SelectField
        id="ce-flow-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(FLOW_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.knownLabel}
        name="ce-flow-known"
        onChange={setKnown}
        options={[
          { value: "rejected", label: strings.knownRejected },
          { value: "work", label: strings.knownWork },
        ]}
        value={known}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="ce-flow"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={{}}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            {value.violatesSecondLaw ? (
              <ErrorNotice>{strings.violates}</ErrorNotice>
            ) : (
              <Notice>
                {value.reversible ? strings.reversible : strings.irreversible}
              </Notice>
            )}
            <StatGrid>
              <StatCard
                emphasis
                label={strings.workDone}
                value={`${formatSig(fmt, value.work, sig)} J`}
              />
              <StatCard
                label={strings.actualEfficiency}
                value={`${formatSig(fmt, value.efficiency * 100, sig)}%`}
              />
              <StatCard
                label={strings.maxEfficiency}
                value={`${formatSig(fmt, value.carnotEfficiency * 100, sig)}%`}
              />
              <StatCard
                label={strings.shareOfCarnot}
                value={
                  Number.isFinite(value.secondLawEfficiency)
                    ? `${formatSig(fmt, value.secondLawEfficiency * 100, sig)}%`
                    : "—"
                }
              />
              <StatCard
                label={strings.maxWork}
                value={`${formatSig(fmt, value.maxWork, sig)} J`}
              />
              <StatCard
                label={strings.entropyGenerated}
                value={`${formatSig(fmt, value.entropyGenerated, sig)} J/K`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={flowWorking(value, fmt, sig)}
            />
            <DiagramFrame caption={strings.engineCaption}>
              <ReservoirDiagram
                coldShare={value.heatOut / value.heatIn}
                description={strings.engineDescription}
                hotShare={1}
                id="ce-flow-diagram"
                label={strings.engineLabel}
                mode="engine"
                workShare={value.work / value.heatIn}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}
